import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  orgManagementService,
  Organization,
  AdminAccount,
} from '../services/orgManagementService';
import { School } from '../types';
import { useActingOrg } from '../context/ActingOrgContext';
import BrandingSettingsModal from '../components/BrandingSettingsModal';
import ResetAdminPasswordModal from '../components/ResetAdminPasswordModal';

const OrgManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { enterOrg } = useActingOrg();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 新建机构
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', code: '', contactName: '', contactPhone: '' });
  const [savingOrg, setSavingOrg] = useState(false);

  // 新建 admin
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [adminForm, setAdminForm] = useState({ username: '', password: '', displayName: '', organizationId: '' });
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [passwordResetAdmin, setPasswordResetAdmin] = useState<AdminAccount | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 学校分配（每行选中的机构）
  const [schoolAssign, setSchoolAssign] = useState<Record<number, string>>({});

  // 品牌设置
  const [brandingOrg, setBrandingOrg] = useState<Organization | null>(null);

  const extractErr = (e: any, fallback: string): string =>
    e?.response?.data?.message || e?.message || fallback;

  const loadOrgs = useCallback(async () => {
    setOrgs(await orgManagementService.listOrgs());
  }, []);

  const loadAdmins = useCallback(async () => {
    setAdmins(await orgManagementService.listAdmins());
  }, []);

  const loadSchools = useCallback(async () => {
    setSchools(await orgManagementService.listSchools());
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadOrgs(), loadAdmins(), loadSchools()]);
    } catch (e) {
      setError(extractErr(e, '加载失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  }, [loadOrgs, loadAdmins, loadSchools]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgForm.name.trim()) {
      setError('机构名称不能为空');
      return;
    }
    setSavingOrg(true);
    setError(null);
    try {
      await orgManagementService.createOrg({
        name: orgForm.name.trim(),
        code: orgForm.code.trim() || undefined,
        contactName: orgForm.contactName.trim() || undefined,
        contactPhone: orgForm.contactPhone.trim() || undefined,
      });
      setOrgForm({ name: '', code: '', contactName: '', contactPhone: '' });
      setShowOrgForm(false);
      await loadOrgs();
    } catch (e) {
      setError(extractErr(e, '创建机构失败'));
    } finally {
      setSavingOrg(false);
    }
  };

  const handleDeleteOrg = async (org: Organization) => {
    if (!window.confirm(`确认删除机构「${org.name}」？`)) return;
    setError(null);
    try {
      await orgManagementService.deleteOrg(org.id);
      await Promise.all([loadOrgs(), loadAdmins(), loadSchools()]);
    } catch (e) {
      setError(extractErr(e, '删除机构失败'));
    }
  };

  const handleAssignSchool = async (schoolId: number) => {
    const raw = schoolAssign[schoolId];
    const organizationId = raw && raw !== '' ? Number(raw) : null;
    setError(null);
    try {
      await orgManagementService.assignSchool(schoolId, organizationId);
      setSchoolAssign((prev) => {
        const next = { ...prev };
        delete next[schoolId];
        return next;
      });
      await Promise.all([loadOrgs(), loadSchools()]);
    } catch (e) {
      setError(extractErr(e, '分配学校失败'));
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminForm.username.trim() || !adminForm.password.trim() || !adminForm.organizationId) {
      setError('请填写用户名、密码并选择所属机构');
      return;
    }
    setSavingAdmin(true);
    setError(null);
    try {
      await orgManagementService.createAdmin({
        username: adminForm.username.trim(),
        password: adminForm.password,
        displayName: adminForm.displayName.trim() || adminForm.username.trim(),
        organizationId: Number(adminForm.organizationId),
      });
      setAdminForm({ username: '', password: '', displayName: '', organizationId: '' });
      setShowAdminForm(false);
      await Promise.all([loadAdmins(), loadOrgs()]);
    } catch (e) {
      setError(extractErr(e, '创建管理员失败'));
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleToggleAdminStatus = async (admin: AdminAccount) => {
    const next = admin.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    setError(null);
    try {
      await orgManagementService.updateAdminStatus(admin.id, next);
      await loadAdmins();
    } catch (e) {
      setError(extractErr(e, '更新管理员状态失败'));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">机构与权限</h1>
        <p className="text-sm text-gray-500 mt-1">管理机构、分配学校、维护管理员账号（仅超级管理员可见）</p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4">关闭</button>
        </div>
      )}

      {success && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-4 text-green-500 hover:text-green-700">关闭</button>
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 py-12 text-center">加载中...</div>
      ) : (
        <div className="space-y-8">
          {/* 机构列表 */}
          <section className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">机构列表</h2>
              <button
                onClick={() => setShowOrgForm((v) => !v)}
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
              >
                {showOrgForm ? '取消' : '新建机构'}
              </button>
            </div>

            {showOrgForm && (
              <form onSubmit={handleCreateOrg} className="px-6 py-4 border-b border-gray-100 bg-gray-50 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">机构名称 *</label>
                  <input
                    value={orgForm.name}
                    onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="机构名称"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">机构编码</label>
                  <input
                    value={orgForm.code}
                    onChange={(e) => setOrgForm({ ...orgForm, code: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="可选"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">联系人</label>
                  <input
                    value={orgForm.contactName}
                    onChange={(e) => setOrgForm({ ...orgForm, contactName: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="可选"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    value={orgForm.contactPhone}
                    onChange={(e) => setOrgForm({ ...orgForm, contactPhone: e.target.value })}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="联系电话(可选)"
                  />
                  <button
                    type="submit"
                    disabled={savingOrg}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {savingOrg ? '保存中' : '保存'}
                  </button>
                </div>
              </form>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-6 py-3 font-medium">名称</th>
                    <th className="px-6 py-3 font-medium">编码</th>
                    <th className="px-6 py-3 font-medium">管理员数</th>
                    <th className="px-6 py-3 font-medium">学校数</th>
                    <th className="px-6 py-3 font-medium">状态</th>
                    <th className="px-6 py-3 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.length === 0 ? (
                    <tr><td colSpan={6} className="px-6 py-6 text-center text-gray-400">暂无机构</td></tr>
                  ) : (
                    orgs.map((org) => (
                      <tr key={org.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-900">{org.name}</td>
                        <td className="px-6 py-3 text-gray-500">{org.code || '-'}</td>
                        <td className="px-6 py-3 text-gray-700">{org.adminCount}</td>
                        <td className="px-6 py-3 text-gray-700">{org.schoolCount}</td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${org.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {org.status}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => { enterOrg({ id: org.id, name: org.name }); navigate('/dashboard'); }}
                              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              进入管理
                            </button>
                            <button
                              onClick={() => setBrandingOrg(org)}
                              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              品牌设置
                            </button>
                            <button onClick={() => handleDeleteOrg(org)} className="text-red-600 hover:text-red-800 text-sm">删除</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* 学校分配 */}
          <section className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">学校分配</h2>
              <p className="text-xs text-gray-500 mt-1">将学校分配到机构（选择「未分配」可解除归属）</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-6 py-3 font-medium">学校</th>
                    <th className="px-6 py-3 font-medium">分配到机构</th>
                    <th className="px-6 py-3 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {schools.length === 0 ? (
                    <tr><td colSpan={3} className="px-6 py-6 text-center text-gray-400">暂无学校</td></tr>
                  ) : (
                    schools.map((s) => (
                      <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-900">{s.name}</td>
                        <td className="px-6 py-3">
                          <select
                            value={schoolAssign[s.id] ?? (s.organizationId != null ? String(s.organizationId) : '')}
                            onChange={(e) => setSchoolAssign({ ...schoolAssign, [s.id]: e.target.value })}
                            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                          >
                            <option value="">未分配</option>
                            {orgs.map((org) => (
                              <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-3">
                          <button
                            onClick={() => handleAssignSchool(s.id)}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700"
                          >
                            保存
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Admin 账号 */}
          <section className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">管理员账号</h2>
              <button
                onClick={() => setShowAdminForm((v) => !v)}
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
              >
                {showAdminForm ? '取消' : '新建管理员'}
              </button>
            </div>

            {showAdminForm && (
              <form onSubmit={handleCreateAdmin} className="px-6 py-4 border-b border-gray-100 bg-gray-50 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">用户名 *</label>
                  <input
                    value={adminForm.username}
                    onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">密码 *</label>
                  <input
                    type="password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">显示名</label>
                  <input
                    value={adminForm.displayName}
                    onChange={(e) => setAdminForm({ ...adminForm, displayName: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="可选"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">所属机构 *</label>
                  <select
                    value={adminForm.organizationId}
                    onChange={(e) => setAdminForm({ ...adminForm, organizationId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">请选择</option>
                    {orgs.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={savingAdmin}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingAdmin ? '保存中' : '保存'}
                  </button>
                </div>
              </form>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-6 py-3 font-medium">用户名</th>
                    <th className="px-6 py-3 font-medium">显示名</th>
                    <th className="px-6 py-3 font-medium">所属机构</th>
                    <th className="px-6 py-3 font-medium">状态</th>
                    <th className="px-6 py-3 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-6 text-center text-gray-400">暂无管理员</td></tr>
                  ) : (
                    admins.map((a) => (
                      <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-900">{a.username}</td>
                        <td className="px-6 py-3 text-gray-700">{a.displayName}</td>
                        <td className="px-6 py-3 text-gray-500">{a.organizationName || '-'}</td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${a.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3 whitespace-nowrap">
                            <button
                              onClick={() => setPasswordResetAdmin(a)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              重置密码
                            </button>
                            <button
                              onClick={() => handleToggleAdminStatus(a)}
                              className={`text-sm ${a.status === 'ACTIVE' ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'}`}
                            >
                              {a.status === 'ACTIVE' ? '停用' : '启用'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {brandingOrg && (
        <BrandingSettingsModal
          org={brandingOrg}
          onClose={() => setBrandingOrg(null)}
          onSaved={() => {
            loadOrgs();
            // 若正代入该机构，实时刷新顶部品牌（BrandingProvider 监听此事件）。
            window.dispatchEvent(new Event('acting-org-changed'));
          }}
        />
      )}

      {passwordResetAdmin && (
        <ResetAdminPasswordModal
          admin={passwordResetAdmin}
          onClose={() => setPasswordResetAdmin(null)}
          onSaved={(username) => setSuccess(`管理员 ${username} 的密码已重置`)}
        />
      )}
    </div>
  );
};

export default OrgManagementPage;
